import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSubjects } from './SubjectContext';
import { EnrolledSubject, progressLabel } from './types';

export const ProgressRing: React.FC<{ percent: number; colour: string; size?: number }> = ({
  percent,
  colour,
  size = 36,
}) => {
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg width={size} height={size} role="img" aria-label={`${percent}% complete`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={colour}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - percent / 100)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

const SubjectRow: React.FC<{ subject: EnrolledSubject; active: boolean; onSelect: () => void }> = ({
  subject,
  active,
  onSelect,
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-current={active ? 'true' : undefined}
    className={`flex w-full items-center gap-3 rounded-lg border-l-4 px-3 py-2 text-left transition-colors ${
      active ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
    }`}
    style={{ borderLeftColor: active ? subject.colour : 'transparent' }}
  >
    <ProgressRing percent={subject.enrolment.progress.percent} colour={subject.colour} />
    <span className="min-w-0">
      <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{subject.name}</span>
      <span className="block text-xs text-gray-500 dark:text-gray-400">{progressLabel(subject.enrolment.progress)}</span>
    </span>
  </button>
);

const SubjectList: React.FC<{ onSelected?: () => void }> = ({ onSelected }) => {
  const { visible, hidden, activeId, switchSubject, setHidden } = useSubjects();
  return (
    <div className="space-y-1">
      {visible.map((subject) => (
        <SubjectRow
          key={subject.id}
          subject={subject}
          active={subject.id === activeId}
          onSelect={() => {
            switchSubject(subject.id);
            onSelected?.();
          }}
        />
      ))}
      <Link
        to="/subjects/add"
        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-lg">
          +
        </span>
        Add subject
      </Link>
      {visible.length > 1 && (
        <button
          type="button"
          onClick={() => setHidden(activeId, true)}
          className="px-3 pt-2 text-xs text-gray-500 underline dark:text-gray-400"
        >
          Hide this subject (progress is kept)
        </button>
      )}
      {hidden.length > 0 && (
        <div className="px-3 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Hidden</p>
          {hidden.map((subject) => (
            <div key={subject.id} className="flex items-center justify-between py-1 text-sm text-gray-600 dark:text-gray-300">
              <span>{subject.name}</span>
              <button type="button" onClick={() => setHidden(subject.id, false)} className="text-xs underline">
                Show
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SubjectSidebar: React.FC = () => (
  <aside className="hidden w-64 shrink-0 lg:block" aria-label="My subjects">
    <div className="sticky top-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
      <h2 className="mb-3 px-3 text-sm font-semibold text-gray-900 dark:text-white">My subjects</h2>
      <SubjectList />
    </div>
  </aside>
);

export const SubjectDropdown: React.FC = () => {
  const { active } = useSubjects();
  const [open, setOpen] = useState(false);
  if (!active) return null;
  return (
    <div className="relative lg:hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-sm font-semibold text-white"
        style={{ backgroundColor: active.colour }}
      >
        {active.short_code} ▾
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-2 w-64 rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5 dark:bg-gray-800">
          <p className="mb-2 px-3 text-sm font-semibold text-gray-900 dark:text-white">My subjects</p>
          <SubjectList onSelected={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
};

export const SubjectPathHeader: React.FC = () => {
  const { active } = useSubjects();
  if (!active) return null;
  const { progress } = active.enrolment;
  return (
    <div className="mb-8 rounded-lg bg-white p-5 shadow dark:bg-gray-800" data-testid="subject-path-header">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: active.colour }}>
            {active.name}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{active.tagline}</p>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {progress.answered} of {progress.question_count} answered · {progress.xp} XP
        </span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="h-2 rounded-full" style={{ width: `${progress.percent}%`, backgroundColor: active.colour }} />
      </div>
    </div>
  );
};
